import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleBody from "./components/ArticleBody";
import RelatedArticles from "./components/RelatedArticles";
import ShareButtons from "./components/ShareButtons";
import {
  formatDate,
  formatViews,
  getArticleById,
  getCategoryById,
  getHotArticles,
  getRelatedArticles,
  getSubCategoryName,
} from "@/lib/articles";
import { applyArticleSeo } from "@/lib/seo";

export default function DetailPage() {
  const { id } = useParams();
  const article = getArticleById(id);
  const category = getCategoryById(article?.categoryId);

  useEffect(() => {
    if (article) {
      applyArticleSeo(
        article.title,
        article.summary.slice(0, 150),
        `鲁啊鲁,${getSubCategoryName(article.categoryId, article.subcategory)},${article.tags.join(
          ","
        )}`
      );
    }
  }, [article]);

  const related = useMemo(() => (article ? getRelatedArticles(article, 5) : []), [article]);
  const hotList = useMemo(() => getHotArticles(5), []);

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            资讯不存在或已被删除
          </h1>
          <p className="mt-2 text-sm text-foreground-600">你访问的内容可能已下线或链接有误</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full">
        <div className="w-full px-4 md:px-8 pt-6">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: category?.name || "资讯", to: `/category/${article.categoryId}` },
              { label: article.title },
            ]}
          />
        </div>

        <div className="w-full px-4 md:px-8 mt-6 pb-4">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <article className="lg:col-span-2">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/category/${article.categoryId}`}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 hover:bg-secondary-200 transition-colors"
                >
                  {category?.name}
                </Link>
                <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
                  {getSubCategoryName(article.categoryId, article.subcategory)}
                </span>
              </div>

              <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[32px]">
                {article.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                  {formatDate(article.publishedAt)}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                  {formatViews(article.views)} 阅读
                </span>
              </div>

              <div className="mt-6 h-64 w-full overflow-hidden rounded-lg bg-background-200 md:h-[420px]">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={`${article.title} - 鲁啊鲁`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="mt-8">
                <ArticleBody content={article.content} />
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600 hover:bg-primary-100 hover:text-primary-700 transition-colors"
                  >
                    # {tag}
                  </Link>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-6">
                <Link
                  to={`/category/${article.categoryId}`}
                  className="flex items-center gap-2 rounded-full border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                  返回{category?.name}列表
                </Link>
                <ShareButtons title={article.title} />
              </div>

              <div className="mt-8 lg:hidden">
                <RelatedArticles articles={related} />
              </div>
            </article>

            <aside className="hidden lg:block lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                <RelatedArticles articles={related} />

                <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                  <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                    <i className="ri-fire-line w-5 h-5 flex items-center justify-center text-accent-500"></i>
                    热门阅读
                  </h3>
                  <ul className="mt-4 space-y-3.5">
                    {hotList.map((item, index) => (
                      <li key={item.id}>
                        <Link to={`/detail/${item.id}`} className="group flex items-start gap-3">
                          <span
                            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-xs font-bold ${
                              index === 0
                                ? "bg-primary-500 text-background-50"
                                : "bg-background-200 text-foreground-600"
                            }`}
                          >
                            {index + 1}
                          </span>
                          <span className="line-clamp-2 text-sm text-foreground-800 group-hover:text-primary-600 transition-colors">
                            {item.title}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}